/* ==========================================================================
   Conquest — finder.js
   Five questions, then one explained recommendation, the way a barista would give it:
   which coffee and why · which grind for their setup and why · how much they use ·
   what each bag size means at that rate · whether buying once or subscribing makes sense.
   A. Taste match: finder tags + taste profile only, never collection order or stock of a grind.
   B. Fulfilment, checked separately: the grind is only pre-selected when the brew method maps
      to a grind the chosen coffee really has; if not, the customer is told and offered the
      next closest coffee that does have it — the taste match is never silently swapped.
   Buy once is always the default — a subscription is never selected for the customer.
   ========================================================================== */
(() => {
  const Conquest = (window.Conquest = window.Conquest || {});
  const fmt = (str, vars) => Conquest.template(str, vars);
  const esc = (str) => String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const norm = (v) => String(v || '').trim().toLowerCase();
  const eq = (a, b) => norm(a) === norm(b);
  const list = (items, and) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`);

  /* ---------- Scoring model (documented in docs/12-COFFEE-FINDER.md) ---------- */
  const FLAVOUR_TAGS = { chocolate: ['chocolate', 'caramel'], nutty: ['nutty'], roasty: ['roasted', 'strong'], fruity: ['fruit'] };
  const STRENGTH_TAG = { smooth: 'smooth', darker: 'strong', sweeter: 'sweet' };
  const STYLE_TAGS = ['smooth', 'strong', 'sweet', 'different'];
  const POINTS = { strength: 6, flavour: 3, milk: 2, both: 1, unsure: 2, curious: -2 };
  /* Old homepage links used metaobject handles */
  const METHOD_ALIASES = { 'espresso-machine': 'espresso', 'bean-to-cup': 'grinder', 'grind-at-home': 'grinder', 'aeropress-espresso-style': 'aeropress', 'filter-pour-over': 'filter' };

  /* Sensory fields hold a score ("8 · Medium–dark"), words ("Dark") or both. Words-only values
     are read with the same fixed vocabulary the product page uses, so "Dark" counts as dark. */
  const WORDS = {
    roast: [[/very dark/, 10], [/medium[\s–-]*dark/, 8], [/dark/, 9], [/medium[\s–-]*light/, 4], [/medium/, 6], [/light/, 3]],
    body: [[/full/, 5], [/medium/, 3], [/light/, 2]],
    sweetness: [[/very sweet/, 5], [/sweet/, 4], [/dry/, 1]],
    acidity: [[/very low/, 1], [/low|soft/, 2], [/balanced/, 3], [/very bright/, 5], [/bright/, 4]]
  };
  const sense = (raw, field) => {
    const text = norm(raw);
    if (!text) return 0;
    const n = parseFloat(text);
    if (!Number.isNaN(n)) return n;
    const hit = (WORDS[field] || []).find(([re]) => re.test(text));
    return hit ? hit[1] : 0;
  };

  const planDays = (plan) => {
    const text = [plan.name].concat(plan.options || []).join(' ').toLowerCase();
    const m = text.match(/(\d+)\s*(day|week|month)/);
    if (m) return Number(m[1]) * (m[2] === 'day' ? 1 : m[2] === 'week' ? 7 : 30);
    if (/fortnight/.test(text)) return 14;
    if (/week/.test(text)) return 7;
    if (/month/.test(text)) return 30;
    return null;
  };
  /* "Delivery every 2 weeks" → "every 2 weeks" */
  const planPhrase = (plan) => {
    const m = /every\s+.+$/i.exec(plan.name || '');
    if (m) return m[0].toLowerCase();
    if (plan.options && plan.options[0]) return `every ${String(plan.options[0]).toLowerCase()}`;
    return `(${plan.name})`;
  };

  class CoffeeFinder extends HTMLElement {
    connectedCallback() {
      const cfg = JSON.parse(this.querySelector('[data-finder-config]').textContent);
      /* Translation text arrives HTML-escaped by Shopify's t filter; decode it to plain text. */
      if (Conquest.decodeText) { Conquest.decodeText(cfg.strings); Conquest.decodeText(cfg.methods); }
      this.cfg = cfg;
      cfg.products.forEach((p) => this.normaliseOptions(p));
      this.S = this.cfg.strings;
      this.form = this.querySelector('[data-finder-form]');
      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.result = this.querySelector('[data-finder-result]');
      this.nextBtn = this.querySelector('[data-finder-next]');
      this.backBtn = this.querySelector('[data-finder-back]');
      this.error = this.querySelector('[data-finder-error]');
      this.cups = this.form.querySelector('[data-cups]');
      this.index = 0;
      this.started = false;
      this.error.hidden = true;

      this.nextBtn.addEventListener('click', (e) => { e.preventDefault(); this.next(); });
      this.backBtn.addEventListener('click', () => this.go(this.index - 1));
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.next(); });

      /* A pointer tap on a single-choice answer moves on gently; keyboard users press Next */
      this.form.addEventListener('click', (e) => {
        if (e.detail === 0) return;
        const answer = e.target.closest('.finder__answer');
        if (!answer || !answer.querySelector('input[type="radio"]')) return;
        const step = answer.closest('[data-step]');
        if (step && step.hasAttribute('data-single')) window.setTimeout(() => { if (this.steps[this.index] === step) this.next(); }, 280);
      });
      this.form.addEventListener('change', () => {
        this.clearError();
        if (!this.started) { this.started = true; Conquest.track('coffee_finder_started', {}); }
      });

      const setCups = (n) => { this.cups.value = Math.max(1, Math.min(12, Math.round(Number(n) || 1))); this.updateDaily(); };
      this.querySelector('[data-cups-minus]').addEventListener('click', () => setCups(Number(this.cups.value) - 1));
      this.querySelector('[data-cups-plus]').addEventListener('click', () => setCups(Number(this.cups.value) + 1));
      this.cups.addEventListener('input', () => this.updateDaily());
      this.cups.addEventListener('change', () => setCups(this.cups.value));
      this.updateDaily();

      /* Deep link from the homepage question: ?method=espresso */
      let start = 0;
      const params = new URLSearchParams(window.location.search);
      const preset = METHOD_ALIASES[params.get('method')] || params.get('method');
      if (preset) {
        const input = this.form.querySelector(`input[name="method"][value="${CSS.escape(preset)}"]`);
        if (input) { input.checked = true; start = 1; }
      }
      this.go(start, false);
    }

    answers() {
      const fd = new FormData(this.form);
      return {
        method: fd.get('method'),
        milk: fd.get('milk'),
        flavour: fd.get('flavour'),
        strength: fd.get('strength'),
        cups: Math.max(1, Math.min(12, Math.round(Number(fd.get('cups')) || 2)))
      };
    }

    /* Safety net for mislabelled options (an older order showed sizes under "Grind size"):
       if the "grind" option's values are all weights, treat it as the size option instead. */
    normaliseOptions(p) {
      const weighty = (i) => i > -1 && p.variants.length && p.variants.every((v) => /\d\s*(g|kg)\b/i.test(String(v.options[i] || '')));
      if (weighty(p.grindIndex) && !weighty(p.sizeIndex)) {
        const other = p.variants[0].options.findIndex((_, i) => i !== p.grindIndex);
        p.sizeIndex = p.grindIndex;
        p.grindIndex = p.variants[0].options.length > 1 ? other : -1;
      }
    }

    updateDaily() {
      const n = Math.max(1, Math.min(12, Math.round(Number(this.cups.value) || 1)));
      this.querySelector('[data-cups-label]').textContent = n === 1 ? this.S.cupADay : this.S.cupsADay;
      this.querySelector('[data-cups-daily]').textContent = fmt(this.S.dailyAmount, { grams: n * this.cfg.gramsPerCup });
    }

    stepValid(i) {
      const step = this.steps[i];
      if (step.dataset.step === 'amount') return Number(this.cups.value) >= 1;
      return !!step.querySelector('input:checked');
    }

    showError() {
      this.error.textContent = this.S.chooseOne;
      this.error.hidden = false;
    }

    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
    }

    next() {
      if (!this.stepValid(this.index)) { this.showError(); return; }
      if (this.index < this.steps.length - 1) this.go(this.index + 1);
      else this.showResult();
    }

    go(i, focus = true) {
      if (i < 0) return;
      this.index = i;
      this.toggleAttribute('data-started', i > 0);
      this.result.hidden = true;
      this.form.hidden = false;
      this.clearError();
      this.steps.forEach((step, n) => { step.hidden = n !== i; });
      const total = this.steps.length;
      this.backBtn.hidden = i === 0;
      this.nextBtn.textContent = i === total - 1 ? this.S.showResult : this.S.next;
      this.querySelector('[data-finder-count]').textContent = fmt(this.S.stepOf, { step: i + 1, total });
      this.querySelector('[data-finder-bar]').style.transform = `scaleX(${(i + 1) / total})`;
      this.renderTrail();
      if (focus) this.steps[i].querySelector('legend').focus({ preventScroll: true });
      if (focus) this.scrollToTop();
    }

    scrollToTop() {
      const top = this.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.5) {
        this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    }

    renderTrail() {
      const trail = this.querySelector('[data-finder-trail]');
      const items = [];
      this.steps.forEach((step, n) => {
        if (n >= this.index && this.result.hidden) return;
        let label = '';
        if (step.dataset.step === 'amount') {
          const c = Number(this.cups.value);
          label = `${c} ${c === 1 ? this.S.cupADay : this.S.cupsADay}`;
        } else {
          const checked = step.querySelector('input:checked');
          if (checked) label = checked.closest('label').querySelector('.finder__answer-label').textContent.trim();
        }
        if (!label) return;
        items.push(`<li><button type="button" class="finder__chip" data-edit="${n}"><span>${esc(label)}</span><span class="visually-hidden"> — ${esc(this.S.edit)}</span></button></li>`);
      });
      trail.innerHTML = items.join('');
      trail.querySelectorAll('[data-edit]').forEach((b) => b.addEventListener('click', () => this.go(Number(b.dataset.edit))));
    }

    /* ---------- Scoring ---------- */
    mappedGrind(method) {
      const m = this.cfg.methods[method];
      return m && m.grind ? m.grind : '';
    }

    grindsFor(p) {
      if (p.grindIndex < 0) return [];
      return Array.from(new Set(p.variants.map((v) => v.options[p.grindIndex])));
    }

    /* Every point is explained, so the result — and the QA test matrix — can say why a coffee won */
    /* A. Taste match only — the brew method and grind availability play no part here */
    score(p, a) {
      const has = (t) => (p.tags || []).some((x) => eq(x, t));
      const tagged = (p.tags || []).length > 0;
      const parts = [];
      const add = (pts, why) => { if (pts) parts.push({ pts, why }); };

      /* Q4 richness — the strongest signal */
      const styleTag = STRENGTH_TAG[a.strength];
      if (styleTag) {
        if (has(styleTag)) add(POINTS.strength, `tag:${styleTag}`);
        else if (!tagged) {
          if (a.strength === 'darker' && sense(p.roast, 'roast') >= 8) add(3, 'roast>=8 (untagged)');
          if (a.strength === 'sweeter' && sense(p.sweetness, 'sweetness') >= 4) add(3, 'sweetness>=4 (untagged)');
          if (a.strength === 'smooth' && sense(p.acidity, 'acidity') && sense(p.acidity, 'acidity') <= 2) add(3, 'acidity<=2 (untagged)');
        }
        /* Taste profile nudges (max +3): darker → roast above 7; sweeter → sweetness above 3; smooth → low acidity */
        if (a.strength === 'darker') add(Math.max(0, Math.min(3, sense(p.roast, 'roast') - 7)), 'roast profile');
        if (a.strength === 'sweeter') add(Math.max(0, Math.min(2, sense(p.sweetness, 'sweetness') - 3)), 'sweetness profile');
        if (a.strength === 'smooth') { const ac = sense(p.acidity, 'acidity'); add(ac && ac <= 2 ? 1 : 0, 'low acidity'); }
      } else if (a.strength === 'not_sure' && has('smooth')) {
        add(POINTS.unsure, 'unsure → approachable (tag:smooth)');
      }

      /* Q3 flavour */
      (FLAVOUR_TAGS[a.flavour] || []).forEach((t) => {
        if (has(t)) add(POINTS.flavour, `tag:${t}`);
        else if (!tagged && p.shorthand && norm(p.shorthand).includes(t)) add(1, `shorthand:${t}`);
      });

      /* Q2 milk */
      if (a.milk === 'milk' && has('milk')) add(POINTS.milk, 'tag:milk');
      if (a.milk === 'black' && has('black')) add(POINTS.milk, 'tag:black');
      if (a.milk === 'both') { if (has('milk')) add(POINTS.both, 'tag:milk'); if (has('black')) add(POINTS.both, 'tag:black'); }

      /* Keep limited / curiosity releases for people who asked for something different */
      if ((p.range === 'curiosity' || p.range === 'limited') && a.flavour !== 'fruity' && !has(STRENGTH_TAG[a.strength] || '-')) add(POINTS.curious, 'curiosity release, not asked for');

      return { score: parts.reduce((t, x) => t + x.pts, 0), parts };
    }

    /* Tie-break (deliberate, never collection order):
       1. fewer style traits the customer didn't ask for (e.g. don't hand a "sweeter" coffee to someone unsure)
       2. more of the customer's answers matched
       3. title A–Z, so the result is stable */
    rank(a) {
      const wantedStyle = a.strength === 'not_sure' ? 'smooth' : STRENGTH_TAG[a.strength];
      const pool = this.cfg.products.filter((p) => p.available);
      const products = pool.length ? pool : this.cfg.products;
      return products.map((p) => {
        const s = this.score(p, a);
        const unrequested = (p.tags || []).filter((t) => STYLE_TAGS.includes(norm(t)) && norm(t) !== wantedStyle).length;
        return { p, score: s.score, parts: s.parts, unrequested, matched: s.parts.filter((x) => x.pts > 0).length };
      }).sort((x, y) => y.score - x.score || x.unrequested - y.unrequested || y.matched - x.matched || x.p.title.localeCompare(y.p.title));
    }

    /* ---------- Sizes, durations and prices ---------- */
    sizesFor(p) {
      const seen = new Map();
      p.variants.forEach((v) => {
        const label = p.sizeIndex > -1 ? v.options[p.sizeIndex] : v.options[0];
        if (!seen.has(label)) seen.set(label, { label, grams: v.grams || 0, price: v.price });
        else seen.get(label).price = Math.min(seen.get(label).price, v.price);
      });
      return Array.from(seen.values()).sort((x, y) => x.grams - y.grams);
    }

    days(grams, perDay) { return grams / perDay; }

    /* "about 17 days" — rounded to the nearest day, never inflated */
    duration(grams, perDay) {
      const S = this.S;
      const d = grams / perDay;
      if (d < 1) return S.lessThanDay;
      const n = Math.round(d);
      return n === 1 ? S.dayOne : fmt(S.days, { n });
    }

    money(cents) {
      const format = (Conquest.settings && Conquest.settings.moneyFormat) || '';
      if (cents < 100 && /£/.test(format || '£')) return `${Math.round(cents)}p`;
      return Conquest.formatMoney(Math.round(cents));
    }

    perCup(size) { return size.grams ? size.price / (size.grams / this.cfg.gramsPerCup) : null; }

    /* Only pre-select a size when something justifies it: one size only, or the merchant's rule */
    recommendSize(sizes, perDay) {
      if (!sizes.length) return null;
      if (sizes.length === 1) return { size: sizes[0], basis: 'only' };
      const rule = Number(this.cfg.targetDays) > 0 ? Number(this.cfg.targetDays) : 0;
      const weighed = sizes.filter((s) => s.grams);
      if (!rule || !weighed.length) return { size: null, basis: 'open' };
      const lasting = weighed.find((s) => s.grams / perDay >= rule);
      return lasting ? { size: lasting, basis: 'rule' } : { size: weighed[weighed.length - 1], basis: 'rule', short: true };
    }

    /* The least frequent delivery that still arrives before the bag runs out (10% leeway).
       null when even the most frequent delivery is too far apart for this bag. */
    fittingPlan(p, days) {
      if (!days) return null;
      const dated = p.plans.map((pl) => ({ pl, d: planDays(pl) })).filter((x) => x.d && x.d <= days * 1.1);
      if (!dated.length) return null;
      return dated.sort((x, y) => y.d - x.d)[0].pl;
    }

    savingPct(p, sizeLabel, planId) {
      const vs = p.variants.filter((v) => p.sizeIndex < 0 || !sizeLabel || eq(v.options[p.sizeIndex], sizeLabel));
      let best = 0;
      vs.forEach((v) => v.allocations.forEach((al) => {
        if (planId && al.planId !== planId) return;
        if (v.price > 0) best = Math.max(best, Math.round(((v.price - al.price) / v.price) * 100));
      }));
      return best;
    }

    findVariant(p, sizeLabel, grindLabel) {
      return p.variants.find((v) =>
        (p.sizeIndex < 0 || eq(v.options[p.sizeIndex], sizeLabel)) && (p.grindIndex < 0 || eq(v.options[p.grindIndex], grindLabel))
      ) || null;
    }

    grindDescriptor(g) {
      const m = Object.values(this.cfg.methods).find((x) => x.grind && eq(x.grind, g) && x.grindLabel);
      return m ? m.grindLabel : '';
    }

    /* ---------- Result ---------- */
    showResult() {
      const a = this.answers();
      const ranked = this.rank(a);
      this.ranking = ranked;
      const best = ranked[0].p;
      const runnerUp = ranked.find((r) => r.p.id !== best.id);
      const tie = !!(runnerUp && runnerUp.score === ranked[0].score);
      const perDay = a.cups * this.cfg.gramsPerCup;
      const sizes = this.sizesFor(best);
      const rec = this.recommendSize(sizes, perDay);
      const grinds = this.grindsFor(best);
      /* B. Fulfilment, separate from the taste match */
      const wanted = this.mappedGrind(a.method);
      const offers = (p) => this.grindsFor(p).some((g) => eq(g, wanted));
      let grind = '';
      if (grinds.length === 1) grind = grinds[0];
      else if (wanted) grind = grinds.find((g) => eq(g, wanted)) || '';
      const unavailable = !!(wanted && grinds.length && !offers(best));
      const nextWithGrind = unavailable ? (ranked.find((r) => r.p.id !== best.id && offers(r.p)) || {}).p || null : null;

      this.state = {
        product: best, answers: a, perDay, sizes, grinds,
        size: rec && rec.size ? rec.size.label : null,
        pick: rec && rec.basis === 'rule' && rec.size ? rec.size.label : null,
        rec, grind, purchase: 'once', planId: null, planTouched: false, unavailable, nextWithGrind
      };
      this.renderResult(a, best, nextWithGrind || (runnerUp ? runnerUp.p : null), tie && !nextWithGrind);
      Conquest.track('coffee_finder_completed', { product_id: best.id, method: a.method, strength: a.strength, flavour: a.flavour, milk: a.milk, cups: a.cups, grind_unavailable: unavailable });
    }

    coffeeCopy(a, p, alt, tie) {
      const S = this.S;
      const phrases = [];
      if (a.milk && a.milk !== 'not_sure') phrases.push(S[`phrase_milk_${a.milk}`]);
      if (a.flavour && a.flavour !== 'not_sure') phrases.push(S[`phrase_flavour_${a.flavour}`]);
      if (a.strength && a.strength !== 'not_sure') phrases.push(S[`phrase_strength_${a.strength}`]);
      const tasteKnown = (a.flavour && a.flavour !== 'not_sure') || (a.strength && a.strength !== 'not_sure');
      const lines = [];
      lines.push(tasteKnown && phrases.length ? fmt(S.startWith, { product: p.title, answers: list(phrases.filter(Boolean), S.and) }) : fmt(S.startUnsure, { product: p.title }));
      if (p.summary) lines.push(p.summary);
      /* Be honest when nothing in the range has the flavour they asked for */
      const wantedTags = FLAVOUR_TAGS[a.flavour] || [];
      /* Only claimed when every coffee is tagged — untagged coffees might have the flavour */
      const allTagged = this.cfg.products.every((x) => (x.tags || []).length);
      if (wantedTags.length && allTagged && !this.cfg.products.some((x) => (x.tags || []).some((t) => wantedTags.some((w) => eq(t, w))))) lines.push(S[`flavourNone_${a.flavour}`]);
      else if (tie && alt) lines.push(fmt(S.closeCall, { product: alt.title }));
      return lines;
    }

    setupCopy(a, p) {
      const S = this.S;
      const m = this.cfg.methods[a.method] || this.cfg.methods.other;
      const grinds = this.state.grinds;
      if (!grinds.length) return [];
      if (grinds.length === 1) return [fmt(S.grindOnly, { product: p.title, grind: grinds[0] })];
      if (a.method === 'other' || !this.cfg.methods[a.method]) return [S.grindOther];
      if (!m.grind) return [fmt(S.grindUnset, { method: m.label.toLowerCase() })];
      if (this.state.unavailable) {
        const method = m.label.toLowerCase();
        const next = this.state.nextWithGrind;
        return [fmt(S.grindUnavailable, { product: p.title, method }), next ? fmt(S.grindUnavailableNext, { product: next.title, method }) : fmt(S.grindUnavailableNone, { method })];
      }
      return [m.why, fmt(S.grindSend, { grind: this.state.grind })];
    }

    usageCopy(a) {
      const S = this.S;
      const st = this.state;
      const g = this.cfg.gramsPerCup;
      const lines = [];
      lines.push(a.cups === 1 ? fmt(S.usageCalcOne, { grams: g, total: st.perDay }) : fmt(S.usageCalc, { cups: a.cups, grams: g, total: st.perDay }));
      lines.push(fmt(S.usageDose, { grams: g }));
      const weighed = st.sizes.filter((s) => s.grams);
      if (!weighed.length) return lines;
      const rec = st.rec;
      if (rec.basis === 'only') lines.push(fmt(S.sizeOnly, { size: rec.size.label, duration: this.duration(rec.size.grams, st.perDay) }));
      else if (rec.basis === 'rule') lines.push(rec.short ? fmt(S.sizeRuleShort, { size: rec.size.label, duration: this.duration(rec.size.grams, st.perDay) }) : fmt(S.sizeRule, { size: rec.size.label, days: this.cfg.targetDays }));
      else {
        /* Duration leads; cost per cup stays in the size tiles as context, never as the pick */
        const WEEK = 7;
        const days = (x) => x.grams / st.perDay;
        const large = weighed[weighed.length - 1];
        const often = weighed.find((x) => days(x) >= WEEK);
        lines.push(S.sizeList);
        if (!often || often === large) {
          if (weighed.length > 1) lines.push(fmt(S.sizeFast, { size: large.label, duration: this.duration(large.grams, st.perDay) }));
        } else {
          const middle = often !== weighed[0];
          lines.push(fmt(middle ? S.sizeMiddle : S.sizeLittleOften, { size: often.label, duration: this.duration(often.grams, st.perDay) }));
          lines.push(fmt(S.sizeFewer, { size: large.label, duration: this.duration(large.grams, st.perDay) }));
        }
        lines.push(S.sizeOpen);
      }
      return lines;
    }

    renderResult(a, p, alt, tie) {
      const S = this.S;
      const st = this.state;
      const uid = `f${Date.now().toString(36)}`;
      const para = (l) => `<p>${esc(l)}</p>`;
      const coffee = this.coffeeCopy(a, p, alt, tie);
      const setup = this.setupCopy(a, p);
      const usage = this.usageCopy(a);
      const hasPlans = p.plans.length > 0;
      const saving = hasPlans ? this.savingPct(p, null, null) : 0;
      const showSizes = st.sizes.length > 1;
      const showGrinds = st.grinds.length > 1;

      this.result.innerHTML = `
        <article class="finder__rec" aria-labelledby="${uid}-title">
          <div class="finder__rec-head${p.image ? '' : ' finder__rec-head--no-media'}">
            ${p.image ? `<div class="finder__rec-media"><img src="${esc(p.image)}" alt="${esc(p.imageAlt)}" width="900" height="1125" loading="lazy"></div>` : ''}
            <div class="finder__rec-name">
              <p class="eyebrow eyebrow--rule">${esc(S.coffeeHeading)}</p>
              <h2 class="finder__rec-title" id="${uid}-title">${esc(p.title)}</h2>
              ${p.shorthand ? `<p class="finder__rec-taste">${esc(p.shorthand)}</p>` : ''}
            </div>
            <div class="finder__rec-copy">${coffee.map(para).join('')}</div>
          </div>

          <div class="finder__panels">
            ${setup.length ? `
              <section class="finder__panel" aria-labelledby="${uid}-setup">
                <h3 class="finder__panel-title" id="${uid}-setup">${esc(S.setupHeading)}</h3>
                ${setup.map(para).join('')}
              </section>` : ''}
            <section class="finder__panel" aria-labelledby="${uid}-usage">
              <h3 class="finder__panel-title" id="${uid}-usage">${esc(S.usageHeading)}</h3>
              ${usage.map(para).join('')}
            </section>
          </div>

          <form class="finder__buy" data-finder-buy novalidate aria-labelledby="${uid}-buy">
            <h3 class="finder__panel-title" id="${uid}-buy">${esc(S.buyHeading)}</h3>
            ${showSizes ? `
              <fieldset class="finder__group" data-group="size">
                <legend class="finder__group-label">${esc(S.sizeLegend)}</legend>
                <div class="finder__options finder__options--sizes">
                  ${st.sizes.map((s, i) => `
                    <label class="finder__option">
                      <input type="radio" name="${uid}-size" value="${esc(s.label)}" data-buy-size ${eq(s.label, st.size) ? 'checked' : ''}>
                      <span class="finder__option-box">
                        <span class="finder__option-name">${esc(s.label)}</span>
                        ${s.grams ? `<span class="finder__option-sub">${esc(fmt(S.lasts, { duration: this.duration(s.grams, st.perDay) }))}</span>` : ''}
                        ${s.grams ? `<span class="finder__option-sub">${esc(fmt(S.perCup, { price: this.money(this.perCup(s)) }))}</span>` : ''}
                        ${eq(s.label, st.pick) ? `<span class="finder__option-badge">${esc(S.myPick)}</span>` : ''}
                      </span>
                    </label>`).join('')}
                </div>
              </fieldset>` : ''}
            ${showGrinds ? `
              <fieldset class="finder__group" data-group="grind">
                <legend class="finder__group-label">${esc(S.grindLegend)}</legend>
                <div class="finder__options finder__options--grinds">
                  ${st.grinds.map((g) => {
                    const d = this.grindDescriptor(g);
                    return `
                    <label class="finder__option">
                      <input type="radio" name="${uid}-grind" value="${esc(g)}" data-buy-grind ${eq(g, st.grind) ? 'checked' : ''}>
                      <span class="finder__option-box">
                        <span class="finder__option-name">${esc(g)}</span>
                        ${d ? `<span class="finder__option-sub">${esc(d)}</span>` : ''}
                      </span>
                    </label>`;
                  }).join('')}
                </div>
              </fieldset>` : ''}
            ${hasPlans ? `
              <fieldset class="finder__group" data-group="purchase">
                <legend class="finder__group-label">${esc(S.purchaseLegend)}</legend>
                <div class="finder__options finder__options--purchase">
                  <label class="finder__option">
                    <input type="radio" name="${uid}-purchase" value="once" data-buy-purchase checked>
                    <span class="finder__option-box"><span class="finder__option-name">${esc(S.once)}</span><span class="finder__option-sub">${esc(S.onceHint)}</span></span>
                  </label>
                  <label class="finder__option">
                    <input type="radio" name="${uid}-purchase" value="subscribe" data-buy-purchase>
                    <span class="finder__option-box"><span class="finder__option-name">${esc(S.subscribe)}${saving ? ` <span class="finder__option-save">${esc(fmt(S.subscribeSave, { pct: saving }))}</span>` : ''}</span><span class="finder__option-sub">${esc(S.subscribeHint)}</span></span>
                  </label>
                </div>
                <div class="field finder__frequency" data-frequency hidden>
                  <label class="finder__group-label" for="${uid}-plan">${esc(S.frequency)}</label>
                  <div class="select-wrap"><select class="select" id="${uid}-plan" data-buy-plan>
                    <option value="">${esc(S.chooseFrequency)}</option>
                    ${p.plans.map((pl) => `<option value="${pl.id}">${esc(pl.name)}</option>`).join('')}
                  </select></div>
                </div>
                <p class="finder__advice" data-advice aria-live="polite"></p>
              </fieldset>` : ''}
            <div class="finder__checkout">
              <p class="finder__price" data-buy-price aria-live="polite"></p>
              <p class="finder__missing" data-buy-missing role="alert" hidden></p>
              <button type="submit" class="button button--large button--full" data-buy-button>${esc(S.add)}</button>
              <p class="form-message form-message--error" data-form-error hidden></p>
            </div>
          </form>

          <div class="finder__rec-links">
            <a class="link" href="${esc(p.url)}">${esc(fmt(S.viewProduct, { product: p.title }))}</a>
            <button type="button" class="link" data-finder-restart>${esc(S.changeAnswers)}</button>
            <button type="button" class="link" data-finder-reset>${esc(S.startAgain)}</button>
          </div>
          ${alt ? `
            <aside class="finder__alt" aria-label="${esc(S.alsoConsider)}">
              <p class="eyebrow">${esc(st.nextWithGrind ? fmt(S.altNextGrind, { method: (this.cfg.methods[a.method] || {}).label.toLowerCase() }) : (tie ? S.altClose : S.alsoConsider))}</p>
              <a href="${esc(alt.url)}" class="finder__alt-link">
                <span class="finder__alt-name">${esc(alt.title)}</span>
                ${alt.eyebrow ? `<span class="finder__alt-label">${esc(alt.eyebrow)}</span>` : ''}
                ${alt.shorthand ? `<span class="finder__alt-taste">${esc(alt.shorthand)}</span>` : ''}
              </a>
            </aside>` : ''}
        </article>`;

      this.form.hidden = true;
      this.result.hidden = false;
      this.setAttribute('data-started', '');
      this.querySelector('[data-finder-bar]').style.transform = 'scaleX(1)';
      this.querySelector('[data-finder-count]').textContent = '';
      this.renderTrail();

      const buy = this.result.querySelector('[data-finder-buy]');
      buy.addEventListener('change', (e) => this.onBuyChange(e));
      buy.addEventListener('submit', (e) => { e.preventDefault(); this.addToBag(buy); });
      this.result.querySelector('[data-finder-restart]').addEventListener('click', () => this.go(0));
      this.result.querySelector('[data-finder-reset]').addEventListener('click', () => {
        this.form.reset();
        this.updateDaily();
        this.go(0);
      });
      this.updateBuy();
      this.result.focus({ preventScroll: true });
      this.scrollToTop();
    }

    onBuyChange(e) {
      const st = this.state;
      const t = e.target;
      if (t.matches('[data-buy-size]')) st.size = t.value;
      if (t.matches('[data-buy-grind]')) st.grind = t.value;
      if (t.matches('[data-buy-plan]')) { st.planId = t.value ? Number(t.value) : null; st.planTouched = true; }
      if (t.matches('[data-buy-purchase]')) {
        st.purchase = t.value;
        if (st.purchase === 'once') st.planId = null;
        else if (!st.planTouched) {
          /* Subscribing was the customer's choice; the frequency starts at the one that fits their bag */
          const size = st.sizes.find((s) => eq(s.label, st.size));
          const plan = size && size.grams ? this.fittingPlan(st.product, size.grams / st.perDay) : null;
          st.planId = plan ? plan.id : null;
        }
        const select = this.result.querySelector('[data-buy-plan]');
        if (select && st.purchase === 'subscribe') select.value = st.planId ? String(st.planId) : '';
      }
      if (t.matches('[data-buy-size]') && st.purchase === 'subscribe' && !st.planTouched) {
        const size = st.sizes.find((s) => eq(s.label, st.size));
        const plan = size && size.grams ? this.fittingPlan(st.product, size.grams / st.perDay) : null;
        st.planId = plan ? plan.id : null;
        const select = this.result.querySelector('[data-buy-plan]');
        if (select) select.value = st.planId ? String(st.planId) : '';
      }
      const missing = this.result.querySelector('[data-buy-missing]');
      if (missing && !missing.hidden) this.validate(false);
      this.updateBuy();
    }

    missingItems() {
      const st = this.state;
      const S = this.S;
      const out = [];
      if (st.sizes.length > 1 && !st.size) out.push({ group: 'size', text: S.missingSize });
      if (st.grinds.length > 1 && !st.grind) out.push({ group: 'grind', text: S.missingGrind });
      if (st.purchase === 'subscribe' && !st.planId) out.push({ group: 'purchase', text: S.missingFrequency });
      return out;
    }

    validate(focus) {
      const missing = this.missingItems();
      const el = this.result.querySelector('[data-buy-missing]');
      this.result.querySelectorAll('[data-group]').forEach((g) => g.classList.toggle('is-missing', missing.some((m) => m.group === g.dataset.group)));
      if (!missing.length) { el.hidden = true; el.textContent = ''; return true; }
      el.textContent = fmt(this.S.missing, { list: list(missing.map((m) => m.text), this.S.and) });
      el.hidden = false;
      if (focus) {
        const group = this.result.querySelector(`[data-group="${missing[0].group}"]`);
        const target = missing[0].group === 'purchase' ? group.querySelector('[data-buy-plan]') : group.querySelector('input');
        if (target) target.focus();
      }
      return false;
    }

    updateBuy() {
      const st = this.state;
      const S = this.S;
      const r = this.result;
      const p = st.product;
      const freq = r.querySelector('[data-frequency]');
      if (freq) freq.hidden = st.purchase !== 'subscribe';

      /* Buy once or subscribe — advice, never a default */
      const advice = r.querySelector('[data-advice]');
      if (advice) {
        const size = st.sizes.find((s) => eq(s.label, st.size));
        const plan = st.planId ? p.plans.find((pl) => pl.id === st.planId) : null;
        const lines = [];
        if (!size || !size.grams) lines.push(S.adviceNoSize);
        else if (st.purchase === 'subscribe' && plan && planDays(plan)) {
          const d = size.grams / st.perDay;
          const interval = planDays(plan);
          const smaller = st.sizes.filter((x) => x.grams && x.grams < size.grams && x.grams / st.perDay >= interval)[0];
          const vars = { plan: planPhrase(plan), size: size.label, duration: this.duration(size.grams, st.perDay) };
          if (d * 1.1 < interval) lines.push({ warn: fmt(S.planShort, vars) });
          else if (smaller) lines.push({ warn: fmt(S.planBuildUp, { ...vars, smaller: smaller.label }) });
          else lines.push(fmt(S.planFit, { ...vars, plan: vars.plan.charAt(0).toUpperCase() + vars.plan.slice(1) }));
        } else {
          const fitting = this.fittingPlan(p, size.grams / st.perDay);
          const dated = p.plans.some((pl) => planDays(pl));
          if (fitting) {
            lines.push(fmt(S.adviceSubscribe, { size: size.label, duration: this.duration(size.grams, st.perDay), plan: planPhrase(fitting) }));
            const pct = this.savingPct(p, size.label, fitting.id);
            if (pct) lines.push(fmt(S.adviceSaving, { pct }));
          } else if (dated) {
            lines.push(fmt(S.adviceBigger, { size: size.label, duration: this.duration(size.grams, st.perDay) }));
          }
        }
        advice.innerHTML = lines.map((l) => (typeof l === 'string' ? esc(l) : `<span class="finder__warn">${esc(l.warn)}</span>`)).join(' ');
        advice.hidden = !lines.length;
      }

      /* Price line and button — the button always says "Add my coffee" */
      const priceEl = r.querySelector('[data-buy-price]');
      const button = r.querySelector('[data-buy-button]');
      const v = (st.sizes.length <= 1 || st.size) && (st.grinds.length <= 1 || st.grind)
        ? this.findVariant(p, st.size || (st.sizes[0] && st.sizes[0].label), st.grind || st.grinds[0]) : null;
      if (v) {
        const alloc = st.purchase === 'subscribe' && st.planId ? v.allocations.find((x) => x.planId === st.planId) : null;
        const bits = [st.size, st.grind, st.purchase === 'subscribe' ? S.subscribe : S.once].filter(Boolean);
        priceEl.innerHTML = `<span class="finder__price-config">${esc(bits.join(' · '))}</span> <strong>${esc(Conquest.formatMoney(alloc ? alloc.price : v.price))}</strong>`;
        button.textContent = v.available ? S.add : S.soldOut;
        button.toggleAttribute('disabled', !v.available);
      } else {
        priceEl.textContent = '';
        button.textContent = S.add;
        button.removeAttribute('disabled');
      }
    }

    addToBag(buy) {
      if (!this.validate(true)) return;
      const st = this.state;
      const v = this.findVariant(st.product, st.size || (st.sizes[0] && st.sizes[0].label), st.grind || st.grinds[0]);
      if (!v || !v.available) return;
      const fd = new FormData();
      fd.append('id', v.id);
      fd.append('quantity', 1);
      if (st.purchase === 'subscribe' && st.planId) fd.append('selling_plan', st.planId);
      Conquest.addToCart(fd, { button: buy.querySelector('[data-buy-button]'), errorEl: buy.querySelector('[data-form-error]'), source: 'coffee_finder' });
    }
  }
  if (!customElements.get('coffee-finder')) customElements.define('coffee-finder', CoffeeFinder);
})();
